// Product labels — templates CRUD + print-plan for tomorrow
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Copy, Trash2, Save, X, Printer, RefreshCw, FileText, Package } from 'lucide-react';

function TemplatesTab({ templates, products, loading, refetch }) {
  const [editing, setEditing] = useState(null);
  const [showLink, setShowLink] = useState(null);

  const saveTemplate = async (t) => {
    try {
      if (t.id) await axios.put(`${API}/labels/templates/${t.id}`, t, { headers: getAuthHeaders() });
      else await axios.post(`${API}/labels/templates`, t, { headers: getAuthHeaders() });
      toast.success('Шаблон сохранён');
      setEditing(null); refetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка сохранения'); }
  };

  const deleteTemplate = async (id) => {
    if (!window.confirm('Удалить шаблон?')) return;
    try {
      await axios.delete(`${API}/labels/templates/${id}`, { headers: getAuthHeaders() });
      toast.success('Удалён'); refetch();
    } catch (e) { toast.error('Ошибка'); }
  };

  const duplicate = async (id) => {
    try {
      await axios.post(`${API}/labels/templates/${id}/duplicate`, {}, { headers: getAuthHeaders() });
      toast.success('Дубликат создан'); refetch();
    } catch (e) { toast.error('Ошибка'); }
  };

  const linkProduct = async (productId, templateId) => {
    try {
      await axios.put(`${API}/labels/products/${productId}/template`,
        { label_template_id: templateId }, { headers: getAuthHeaders() });
      toast.success('Обновлено'); refetch();
    } catch (e) { toast.error('Ошибка'); }
  };

  const linkedProducts = useMemo(() => {
    if (!showLink) return { linked: [], available: products };
    const linked = products.filter(p => p.label_template_id === showLink.id);
    const available = products.filter(p => p.label_template_id !== showLink.id);
    return { linked, available };
  }, [products, showLink]);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">Шаблоны наклеек 55×30 мм для термопринтера</div>
        <Button onClick={() => setEditing({ name: '', product_name: '', composition: '', shelf_days: 2, brand_inst: '@ainanan.kz' })}
                className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="labels-new-tpl-btn">
          <Plus className="w-4 h-4 mr-1" /> Новый шаблон
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <div className="grid gap-3">
          {templates.map(t => (
            <Card key={t.id} data-testid={`labels-tpl-${t.id}`}>
              <CardContent className="p-4 flex justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="font-medium">{t.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{t.composition || '—'}</div>
                  <div className="mt-1 flex items-center gap-2 text-xs">
                    <Badge className="bg-slate-100 text-slate-700">{t.shelf_days} дн.</Badge>
                    <Badge className="bg-emerald-100 text-emerald-800">{t.brand_inst}</Badge>
                    <Badge className="bg-sky-100 text-sky-800">
                      <Package className="w-3 h-3 mr-0.5" />{t.linked_products} товара
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1 items-start">
                  <Button size="sm" variant="outline" onClick={() => setShowLink(t)}>
                    <Package className="w-4 h-4 mr-1" />Привязка
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditing(t)}>Изм.</Button>
                  <Button size="sm" variant="outline" onClick={() => duplicate(t.id)} title="Дублировать">
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteTemplate(t.id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {templates.length === 0 && <div className="text-center py-8 text-gray-400">Нет шаблонов</div>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) setEditing(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing?.id ? 'Редактировать шаблон' : 'Новый шаблон'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Внутреннее имя</Label>
                <Input value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} data-testid="labels-field-name" /></div>
              <div><Label>Название (что печатается)</Label>
                <Input value={editing.product_name} onChange={(e) => setEditing({...editing, product_name: e.target.value})} data-testid="labels-field-product-name" /></div>
              <div><Label>Состав</Label>
                <Textarea rows={3} value={editing.composition} onChange={(e) => setEditing({...editing, composition: e.target.value})} data-testid="labels-field-composition" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Срок годности (дней)</Label>
                  <Input type="number" min="1" value={editing.shelf_days} onChange={(e) => setEditing({...editing, shelf_days: Number(e.target.value)})} data-testid="labels-field-shelf" /></div>
                <div><Label>Марка / inst</Label>
                  <Input value={editing.brand_inst} onChange={(e) => setEditing({...editing, brand_inst: e.target.value})} data-testid="labels-field-brand" /></div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditing(null)}><X className="w-4 h-4 mr-1" />Отмена</Button>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => saveTemplate(editing)} data-testid="labels-save-btn">
                  <Save className="w-4 h-4 mr-1" />Сохранить
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!showLink} onOpenChange={(v) => { if (!v) setShowLink(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Привязка товаров: «{showLink?.name}»</DialogTitle></DialogHeader>
          {showLink && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-sm font-medium mb-1">Привязано ({linkedProducts.linked.length})</div>
              <div className="border rounded max-h-80 overflow-y-auto">
                {linkedProducts.linked.map(p => (
                  <button key={p.id} className="w-full text-left px-2 py-1.5 hover:bg-red-50 text-sm border-b last:border-b-0"
                          onClick={() => linkProduct(p.id, null)}>
                    <span className="text-red-500 mr-1">✕</span> {p.name}
                  </button>
                ))}
                {linkedProducts.linked.length === 0 && <div className="text-center text-xs text-gray-400 py-4">Нет</div>}
              </div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Все товары</div>
              <div className="border rounded max-h-80 overflow-y-auto">
                {linkedProducts.available.map(p => (
                  <button key={p.id} className="w-full text-left px-2 py-1.5 hover:bg-emerald-50 text-sm border-b last:border-b-0"
                          onClick={() => linkProduct(p.id, showLink.id)}
                          disabled={!!p.label_template_id && p.label_template_id !== showLink.id}
                          title={p.label_template_id && p.label_template_id !== showLink.id ? `Уже: ${p.label_template_name}` : ''}>
                    <span className={`mr-1 ${p.label_template_id ? 'text-gray-400' : 'text-emerald-600'}`}>+</span>
                    {p.name}
                    {p.label_template_id && p.label_template_id !== showLink.id && (
                      <span className="text-xs text-gray-400 ml-1">({p.label_template_name})</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function PrintTomorrowTab() {
  const [plan, setPlan] = useState({ rows: [], delivery_date: '', manufacturing_date: '' });
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState({});
  const [shelf, setShelf] = useState({});

  const fetch1 = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/labels/print-plan`, { headers: getAuthHeaders() });
      setPlan(r.data);
      const q = {}; const s = {};
      for (const row of r.data.rows || []) { q[row.template_id] = row.forecast_qty; s[row.template_id] = row.shelf_days; }
      setQty(q); setShelf(s);
    } catch (e) { toast.error('Не удалось загрузить план'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch1(); }, []);

  const doPrint = async () => {
    const items = plan.rows
      .map(r => ({ template_id: r.template_id, quantity: qty[r.template_id] || 0, shelf_days: shelf[r.template_id] }))
      .filter(x => x.quantity > 0);
    if (items.length === 0) { toast.error('Нечего печатать'); return; }
    try {
      const r = await axios.post(`${API}/labels/print`, { items }, { headers: getAuthHeaders(), responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success(`Наклеек: ${items.reduce((a, x) => a + x.quantity, 0)}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка печати'); }
  };

  const total = plan.rows.reduce((a, r) => a + (qty[r.template_id] || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <div className="text-sm text-gray-500">Доставка: <b>{plan.delivery_date}</b> · Изготовление: <b>{plan.manufacturing_date}</b></div>
          <div className="text-xs text-gray-400 mt-0.5">Товары без шаблона наклейки не показаны.</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetch1}><RefreshCw className={`w-4 h-4 mr-1 ${loading?'animate-spin':''}`}/>Обновить</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={doPrint} data-testid="labels-print-btn">
            <Printer className="w-4 h-4 mr-1" />Печать ({total})
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : (
        <table className="w-full border">
          <thead className="bg-gray-50 text-sm">
            <tr>
              <th className="px-3 py-2 text-left">Товар</th>
              <th className="px-3 py-2 text-left">Шаблон</th>
              <th className="px-3 py-2 text-right">Прогноз</th>
              <th className="px-3 py-2 text-right">К печати</th>
              <th className="px-3 py-2 text-right">Срок (дн)</th>
            </tr>
          </thead>
          <tbody>
            {plan.rows.map(r => (
              <tr key={r.template_id} className="border-t">
                <td className="px-3 py-2 text-sm">{r.product_name}</td>
                <td className="px-3 py-2 text-xs text-gray-500">{r.template_name}</td>
                <td className="px-3 py-2 text-right text-sm">{r.forecast_qty}</td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" min="0" className="w-24 text-right" value={qty[r.template_id] ?? 0}
                         onChange={(e) => setQty({...qty, [r.template_id]: Number(e.target.value)})}
                         data-testid={`labels-qty-${r.template_id}`} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Input type="number" min="1" className="w-20 text-right" value={shelf[r.template_id] ?? r.shelf_days}
                         onChange={(e) => setShelf({...shelf, [r.template_id]: Number(e.target.value)})} />
                </td>
              </tr>
            ))}
            {plan.rows.length === 0 && (
              <tr><td colSpan="5" className="text-center text-gray-400 py-6">Нет товаров на завтра или ни один не привязан к шаблону</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}


export default function LabelsPage() {
  const [subtab, setSubtab] = useState('print');
  const [templates, setTemplates] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const refetch = async () => {
    try {
      const [t, p] = await Promise.all([
        axios.get(`${API}/labels/templates`, { headers: getAuthHeaders() }),
        axios.get(`${API}/labels/products`,  { headers: getAuthHeaders() }),
      ]);
      setTemplates(t.data.templates || []);
      setProducts(p.data.products || []);
    } catch (e) {
      toast.error('Не удалось загрузить шаблоны');
    } finally { setLoading(false); }
  };
  useEffect(() => { refetch(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-3" data-testid="labels-page">
      <Tabs value={subtab} onValueChange={setSubtab}>
        <TabsList>
          <TabsTrigger value="print" data-testid="labels-tab-print"><Printer className="w-4 h-4 mr-1" />Печать на завтра</TabsTrigger>
          <TabsTrigger value="templates" data-testid="labels-tab-templates"><FileText className="w-4 h-4 mr-1" />Шаблоны</TabsTrigger>
        </TabsList>
        <TabsContent value="print" forceMount hidden={subtab !== 'print'}>
          <PrintTomorrowTab />
        </TabsContent>
        <TabsContent value="templates" forceMount hidden={subtab !== 'templates'}>
          <TemplatesTab templates={templates} products={products} loading={loading} refetch={refetch} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
